// The logic of the findings and devices pages, with no React: how a finding is named, counted and described. The finding itself comes from
// the API (GET v3/findings); nothing here judges anything.

export const SEVERITIES = ['critical', 'high', 'medium', 'low', 'info'];
export const STATUSES = ['drift', 'unknown', 'excepted', 'policyError'];
export const CAPABILITIES = ['software', 'patch', 'cve', 'reboot'];

export const severityClass = (severity) => ({ critical: 'pill-red', high: 'pill-red', medium: 'pill-amber', low: 'pill-blue' }[severity] || 'pill-gray');

const GAPS = {
  missing: 'Missing', outdated: 'Outdated', 'forbidden-present': 'Forbidden software present', oldcopy: 'Old copy', unverified: 'Unverified',
  vulnerable: 'Known vulnerabilities', noDefinition: 'No definition', invalidPolicy: 'Invalid policy', conflict: 'Policy conflict', identityMismatch: 'Identity mismatch', unlisted: 'Unlisted software',
  unknown: 'Cannot tell', unreachable: 'Not reporting', updateServiceOff: 'Update service off', installFailing: 'Updates failing', securityOverdue: 'Security update overdue',
  noRecentInstall: 'No recent update', featureReleaseOld: 'Old Windows release', updatesPaused: 'Updates paused', scanStale: 'No recent scan',
  deferralLong: 'Long deferral', releasePinned: 'Release pinned', rebootPending: 'Restart pending',
  restartNeeded: 'Restart needed', restartOverdue: 'Restart overdue', noticeNotSeen: 'Notices not seen', restartDeferred: 'Restart extended',
  configDrift: 'Restart settings differ',
};
export const gapText = (gap) => GAPS[gap] || gap;

const STATUS_TEXT = { drift: 'Drift', unknown: 'Unknown', excepted: 'Excepted', policyError: 'Policy error' };
export const statusText = (status) => STATUS_TEXT[status] || status;

export const whenText = (utc) => (utc ? String(utc).slice(0, 16).replace('T', ' ') : '');

// What would put it right, in words: "Update (Git.Git)", "Needs a person", "None".
export const actionText = (finding) => {
  if (!finding || !finding.action || finding.action === 'none') return 'None';
  if (finding.action === 'alert') return 'Needs a person';
  const name = finding.action.charAt(0).toUpperCase() + finding.action.slice(1);
  return finding.actionRef && finding.actionRef.definition ? `${name} (${finding.actionRef.definition})` : name;
};

export const countBy = (findings, key) => (findings || []).reduce((acc, f) => ({ ...acc, [f[key]]: (acc[f[key]] || 0) + 1 }), {});

// The open findings of a device, worst first, as {critical: 1, high: 2}: for the devices list.
export const bySeverity = (findings) => {
  const counts = countBy(findings, 'severity');
  return SEVERITIES.filter((s) => counts[s]).map((s) => ({ severity: s, count: counts[s] }));
};

const same = (a, b) => String(a || '').toLowerCase() === String(b || '').toLowerCase();

// The findings that belong to a device (by hostname or device id).
export const findingsOf = (findings, device) => (findings || []).filter((f) => same(f.device, device.hostname) || same(f.deviceId, device.deviceId));

// The page's own search: a finding matches when the text is in its device, subject, gap or what was observed.
export const matchesText = (finding, text) => {
  const wanted = String(text || '').trim().toLowerCase();
  if (!wanted) return true;
  return [finding.device, finding.subject, finding.subjectName, finding.gap, gapText(finding.gap), finding.observed]
    .some((v) => String(v || '').toLowerCase().includes(wanted));
};

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

// The sentence the page shows after "Evaluate now", from POST v3/evaluate's answer.
export const evaluateText = (result) => {
  if (!result) return '';
  const c = result.changes || {};
  const parts = [`Evaluated ${plural(result.machines || 0, 'machine')}: ${(result.summary || {}).findings || 0} open`];
  const moves = [['new', c.new], ['still open', c.still], ['resolved', c.resolved], ['back again', c.reopened]].filter(([, n]) => n);
  if (moves.length) parts.push(moves.map(([word, n]) => `${n} ${word}`).join(', '));
  const none = Object.keys(result.withoutResult || {}).filter((k) => (result.withoutResult[k] || []).length);
  if (none.length) parts.push(none.map((k) => `${plural(result.withoutResult[k].length, 'machine')} with no ${k} result`).join(', '));
  if ((result.errors || []).length) parts.push(`${plural(result.errors.length, 'comparison')} failed`);
  return `${parts[0]}${parts.length > 1 ? ` (${parts.slice(1).join('; ')})` : ''}.`;
};

// ---- the notices of a pending restart (a reboot finding's detail), in words

const NOTICE_PHASES = { daily: 'Daily', lastday: 'Last day', final: 'Final, sticky', restart: 'Restarting' };
export const noticePhaseText = (phase) => NOTICE_PHASES[phase] || phase || '-';

// shown is what the tray answered: true (shown), false (nobody was signed in to see it), null (it could not be sent).
export const noticeShownText = (shown) => (shown === true ? 'Shown' : shown === false ? 'Not shown (nobody signed in)' : 'Not sent');

const CYCLE_REASONS = { deadline: 'the deadline was reached', 'nobody-logged-on': 'nobody was signed in' };

// How the last pending restart ended, from the result's lastCycle: "Restarted by the script (the deadline was reached) at 2026-11-04 14:02, 4 notices, 3 shown".
export const cycleText = (cycle) => {
  if (!cycle || !cycle.outcome) return '';
  const when = whenText(cycle.endedUtc);
  const reason = CYCLE_REASONS[cycle.reason] || cycle.reason;
  const how = cycle.outcome === 'restarting'
    ? `The script asked for a restart${reason ? ` (${reason})` : ''} at ${when}`
    : cycle.outcome === 'restarted'
      ? (cycle.forced ? `Restarted by the script${reason ? ` (${reason})` : ''} at ${when}` : `Restarted by the user at ${when}`)
      : `The pending restart cleared without a restart at ${when}`;
  const n = Number(cycle.notices) || 0;
  return `${how}, ${n} notice${n === 1 ? '' : 's'}, ${Number(cycle.shown) || 0} shown`;
};

// Who was signed in (DOMAIN\user names): the names, "nobody" for an empty list, and "-" when the result does not say (an older script).
export const usersText = (users) => (Array.isArray(users) ? (users.length ? users.join(', ') : 'nobody') : '-');
